<script lang="ts" setup>
import { ref, computed, watch, toRef } from "vue";
import { useRouter } from "vue-router";
import AddSubscriberModal from "@/components/AddSubscriberModal.vue";
import DataView from "@/components/DataView.vue";
import { subscriptionsApi } from "@/services/subscriptions";
import { useProductVersions } from "@/queries/products";
import type { Config } from "datatables.net";

interface Props {
  product: any;
  productStatus: any;
}

const props = defineProps<Props>();
const router = useRouter();

// Cell-render functions return HTML for in-table display. Strip it before sending values to
// the DataTables Buttons exporters so the CSV/Excel/PDF/Copy outputs contain plain text.
const stripHtml = (data: unknown): string =>
  typeof data === 'string' ? data.replace(/<[^>]*>/g, '') : String(data ?? '');

// Subscribing requires a published version — without one, the backend rejects with
// BadRequestError. Block the button at the source so users don't click into an error.
const productIdRef = toRef(() => props.product?.id ?? "");
const { versions } = useProductVersions(productIdRef);
const hasPublishedVersion = computed(() =>
  versions.value.some((v) => v.status === "Published")
);
const cannotAddSubscriberReason = computed(() =>
  hasPublishedVersion.value
    ? ""
    : "This product has no published version yet — publish a version before adding subscribers."
);

const subscribers = ref<any[]>([]);
const searchQuery = ref("");
const statusFilter = ref("all");
const showAddSubscriberModal = ref(false);
const loading = ref(false);

// Reload whenever the product changes. `onMounted` fired once and missed product switches when
// the user navigates between products in the same detail view — the tab component is reused, not
// remounted. `immediate: true` covers the initial load.
watch(
  () => props.product?.id,
  async (id) => {
    if (!id) {
      subscribers.value = [];
      return;
    }
    await loadSubscribers(id);
  },
  { immediate: true },
);

async function loadSubscribers(productId: string) {
  try {
    loading.value = true;
    subscribers.value = [];
    // Fetch the subscriber list and the per-subscriber run metrics in parallel — metrics endpoint
    // returns one row per subscribing org with real totalRuns / lastRun attribution (via
    // RunHistory.RunningProjectId, captured at run start).
    const [subscriptions, metrics] = await Promise.all([
      subscriptionsApi.getProductSubscribers(productId),
      subscriptionsApi.getProductSubscriberMetrics(productId),
    ]);

    const metricsByOrg = new Map(metrics.map((m) => [m.orgId, m]));

    subscribers.value = subscriptions.map((sub) => {
      const m = metricsByOrg.get(sub.orgId);
      return {
        id: sub.id,
        orgId: sub.orgId,
        organizationName: sub.orgName,
        contactEmail: sub.contactEmail,
        subscribedDate: sub.startDate,
        status: sub.isActive ? "Active" : "Inactive",
        // Empty state for "no runs yet" — distinguish from a real "ran at $time" value so the
        // table doesn't render the literal "N/A" string indefinitely.
        lastRun: m?.lastRun ?? null,
        totalRuns: m?.totalRuns ?? 0,
      };
    });
  } catch (error) {
    console.error("Failed to load subscribers:", error);
  } finally {
    loading.value = false;
  }
}

function openAddSubscriberModal() {
  showAddSubscriberModal.value = true;
}

async function handleSubscriberAdded() {
  if (props.product?.id) await loadSubscribers(props.product.id);
}

const tableOptions = {
  columns: [
    { data: 'organizationName', title: 'Organization', render: (d: string) => `<div class="fw-medium">${d}</div>` },
    { data: 'contactEmail', title: 'Contact Email' },
    { data: 'subscribedDate', title: 'Subscribed', render: (d: string) => formatDate(d) },
    { data: 'status', title: 'Status', render: (d: string) => `<span class="badge ${getStatusClass(d)}">${d}</span>` },
    { data: 'lastRun', title: 'Last Run', render: (d: string | null) => d ? formatDate(d) : '<span class="text-muted">No runs yet</span>' },
    { data: 'totalRuns', title: 'Total Runs', render: (d: number) => d > 0 ? `<span class="badge bg-primary rounded-pill">${d}</span>` : '<span class="text-muted">0</span>' },
  ],
  layout: {
    topEnd: {
      buttons: [
        {
          extend: 'collection',
          text: '<i class="bi bi-download me-1"></i>Export',
          className: 'bb-btn-tertiary-cta btn-sm',
          buttons: [
            { extend: 'csv', text: 'CSV', exportOptions: { format: { body: stripHtml } } },
            { extend: 'excel', text: 'Excel', exportOptions: { format: { body: stripHtml } } },
            { extend: 'pdf', text: 'PDF', exportOptions: { format: { body: stripHtml } } },
            { extend: 'copy', text: 'Copy', exportOptions: { format: { body: stripHtml } } },
          ],
        },
      ],
    },
    bottomEnd: '',
    bottomStart: '',
  },
} as Config;

function getStatusClass(status: string): string {
  switch (status.toLowerCase()) {
    case "active":
      return "bg-success";
    case "paused":
      return "bg-danger";
    case "pending":
      return "bg-warning";
    default:
      return "bg-warning";
  }
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function viewSubscriberDetail(subscriberId: string) {
  router.push({
    name: "publisher-subscriber-detail",
    params: { subscriptionId: subscriberId }
  });
}
</script>

<template>
  <div class="subscribers-tab">
    <AddSubscriberModal
      v-model:show="showAddSubscriberModal"
      :product-id="product.id"
      @subscribed="handleSubscriberAdded"
    />

    <!-- Loading state — covers initial load and the refetch when the product changes. Without
         this the page renders the "No subscribers yet" empty state for a beat while the request
         is in flight, which reads as "this product has none" instead of "still loading." -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-secondary" role="status">
        <span class="visually-hidden">Loading subscribers…</span>
      </div>
      <p class="text-muted small mt-3 mb-0">Loading subscribers…</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="subscribers.length === 0" class="text-center py-5">
      <i class="bi bi-people display-1 text-muted"></i>
      <h3 class="mt-3 text-muted">No subscribers yet</h3>
      <p class="text-muted">
        Once organizations subscribe to this product, they will appear here.
      </p>
      <button
        class="bb-btn-primary-cta mt-2"
        :disabled="!hasPublishedVersion"
        :title="cannotAddSubscriberReason"
        @click="openAddSubscriberModal"
      >
        <i class="bi bi-plus-circle me-1"></i> Add Subscriber
      </button>
      <p v-if="!hasPublishedVersion" class="text-muted small mt-2 mb-0">
        {{ cannotAddSubscriberReason }}
      </p>
    </div>

    <!-- Subscribers List -->
    <template v-else>
      <div class="row g-3 mb-4">
        <div class="col-md-8">
          <input
            type="text"
            class="form-control"
            placeholder="Search subscribers..."
            v-model="searchQuery"
          />
        </div>
        <div class="col-md-4">
          <select class="form-select" v-model="statusFilter">
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h2 class="card-title mb-0">Subscribers ({{ subscribers.length }})</h2>
        <button
          class="bb-btn-primary-cta btn-sm"
          :disabled="!hasPublishedVersion"
          :title="cannotAddSubscriberReason"
          @click="openAddSubscriberModal"
        >
          <i class="bi bi-plus"></i> Add Subscriber
        </button>
      </div>
      <DataView
        :data="subscribers"
        view-mode="table"
        :options="tableOptions"
        @row-click="(row) => viewSubscriberDetail(row.id)"
      >
        <template #grid-card="{ row: subscriber }">
          <div class="card h-100 card-hover">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <h6 class="mb-0 fw-medium">{{ subscriber.organizationName }}</h6>
                <span class="badge" :class="getStatusClass(subscriber.status)">
                  {{ subscriber.status }}
                </span>
              </div>
              <div class="small text-muted mb-2">
                <div><i class="bi bi-envelope me-1"></i>{{ subscriber.contactEmail }}</div>
                <div><i class="bi bi-calendar me-1"></i>Subscribed {{ formatDate(subscriber.subscribedDate) }}</div>
                <div><i class="bi bi-clock me-1"></i>Last run {{ subscriber.lastRun }}</div>
              </div>
              <span class="badge bg-primary rounded-pill">{{ subscriber.totalRuns }} runs</span>
            </div>
          </div>
        </template>
      </DataView>
    </template>
  </div>
</template>
